import { useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { DEMO_MODE } from "@/lib/demoMode";
import { TERMS_REQUIRED_EVENT } from "@/lib/termsGate";
import { AuthContext, useOptionalAuth } from "./authContextValue";

export { useOptionalAuth };

const PROFILE_CACHE = "bricks-os-profile";

function cacheKey(userId) {
  return userId ? `${PROFILE_CACHE}:${userId}` : PROFILE_CACHE;
}

function readCachedProfile(userId) {
  try {
    const raw = (userId && localStorage.getItem(cacheKey(userId))) || localStorage.getItem(PROFILE_CACHE) || sessionStorage.getItem(PROFILE_CACHE);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile) {
  const id = profile?.user_id || profile?.id;
  try {
    if (profile && id) {
      const stored = { ...profile, profile_pending: false };
      localStorage.setItem(cacheKey(id), JSON.stringify(stored));
      localStorage.setItem(PROFILE_CACHE, JSON.stringify(stored));
    } else if (!profile) {
      const keys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PROFILE_CACHE)) keys.push(k);
      }
      keys.forEach((k) => localStorage.removeItem(k));
      sessionStorage.removeItem(PROFILE_CACHE);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function profileFromSession(session) {
  const u = session?.user;
  if (!u) return null;
  const meta = u.user_metadata || {};
  // Phone-OTP users have no email until they confirm one (AddEmailPage).
  const needsEmail = !u.email && !!u.phone;
  return {
    user_id: u.id,
    email: u.email || "",
    phone: u.phone ? `+${String(u.phone).replace(/^\+/, "")}` : "",
    needs_email: needsEmail || undefined,
    name: meta.full_name || meta.name || (u.email || "").split("@")[0],
    role: meta.role,
    account_type: meta.account_type,
    subscription_type: meta.subscription_type,
    is_platform_owner: !!meta.is_platform_owner,
    needs_onboarding: false,
    profile_pending: !meta.subscription_type && !meta.is_platform_owner && meta.role !== "photographer",
  };
}

/** Map a Supabase session to the OS / Dispatch profile used by RoleHome. */
export async function loadUserProfile(session) {
  const role = session.user?.user_metadata?.role;
  if (role === "photographer") {
    return {
      user_id: session.user.id,
      email: session.user.email,
      name: session.user.user_metadata?.full_name || session.user.user_metadata?.name || (session.user.email || "").split("@")[0],
      role: "photographer",
      needs_onboarding: false,
    };
  }
  const { data } = await api.get("/auth/me");
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  const applySession = useCallback(async (next, { initial = false } = {}) => {
    if (DEMO_MODE) {
      const { demoUser } = await import("@/demo/fixtures");
      setUser(demoUser());
      setSession({ access_token: "demo" });
      setLoading(false);
      return;
    }
    setSession(next || null);
    if (!next) {
      setUser(null);
      writeCachedProfile(null);
      setLoading(false);
      return;
    }
    if (initial) {
      setUser((prev) => prev || readCachedProfile(next.user?.id) || profileFromSession(next));
      setLoading(false);
    }
    try {
      const profile = await loadUserProfile(next);
      setUser(profile);
      writeCachedProfile(profile);
    } catch (error) {
      console.error("Auth profile failed:", error);
      setUser((prev) => prev || readCachedProfile(next.user?.id) || profileFromSession(next));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (DEMO_MODE) {
      applySession({ access_token: "demo" }, { initial: true });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data: { session: next } } = await supabase.auth.getSession();
        if (!cancelled) await applySession(next, { initial: true });
      } catch (error) {
        console.error("Auth check failed:", error);
        if (!cancelled) {
          setUser(userRef.current || readCachedProfile());
          setLoading(false);
        }
      }
    })();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, next) => {
      if (event === "INITIAL_SESSION") return;
      if (event === "TOKEN_REFRESHED") {
        setSession(next || null);
        return;
      }
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        writeCachedProfile(null);
        setLoading(false);
        return;
      }
      if (!next) return;
      // Defer: calling Supabase (getSession via the api interceptor) inside this callback
      // deadlocks the auth lock, so every later API call hangs (supabase-js docs).
      setTimeout(() => applySession(next, { initial: false }), 0);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [applySession]);

  // Any API call answering 403 terms_required puts the user on the agreement step.
  useEffect(() => {
    const onTermsRequired = () => setUser((prev) => (prev && !prev.needs_terms ? { ...prev, needs_terms: true } : prev));
    window.addEventListener(TERMS_REQUIRED_EVENT, onTermsRequired);
    return () => window.removeEventListener(TERMS_REQUIRED_EVENT, onTermsRequired);
  }, []);

  // One-time terms + privacy agreement; the server records the time.
  const acceptTerms = async () => {
    if (DEMO_MODE) {
      const next = { ...userRef.current, needs_terms: false };
      setUser(next);
      return next;
    }
    const { data } = await api.post("/auth/accept-terms");
    const next = { ...(userRef.current || {}), ...data, needs_terms: false };
    setUser(next);
    writeCachedProfile(next);
    return next;
  };

  const login = async (email, password) => {
    if (DEMO_MODE) {
      const { demoUser } = await import("@/demo/fixtures");
      const u = demoUser();
      setUser(u);
      setSession({ access_token: "demo" });
      return u;
    }
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    setSession(data.session || null);
    const profile = await loadUserProfile(data.session || { user: data.user });
    setUser(profile);
    writeCachedProfile(profile);
    return profile;
  };

  const loginWithPasskey = async () => {
    if (DEMO_MODE) {
      const { demoUser } = await import("@/demo/fixtures");
      const u = demoUser();
      setUser(u);
      setSession({ access_token: "demo" });
      return u;
    }
    const { data, error } = await supabase.auth.signInWithPasskey();
    if (error) throw error;
    const next = data?.session || null;
    if (!next) throw new Error("Passkey sign-in did not return a session.");
    setSession(next);
    const profile = await loadUserProfile(next);
    setUser(profile);
    writeCachedProfile(profile);
    return profile;
  };

  // Phone OTP (and any other flow that already holds a Supabase session).
  const loginWithSession = async (next) => {
    if (!next) throw new Error("Sign-in did not return a session.");
    setSession(next);
    const profile = await loadUserProfile(next);
    setUser(profile);
    writeCachedProfile(profile);
    return profile;
  };

  const register = async (email, password, name, termsAccepted = false) => {
    if (DEMO_MODE) {
      const { demoUser } = await import("@/demo/fixtures");
      const u = demoUser();
      setUser(u);
      setSession({ access_token: "demo" });
      return u;
    }
    const { data } = await api.post("/auth/register", { email, password, name, terms_accepted: !!termsAccepted });
    if (!data.confirm_email) setUser(data);
    return data;
  };

  const logout = async () => {
    if (!DEMO_MODE) await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    writeCachedProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, session, loading, login, loginWithPasskey, loginWithSession, register, logout, acceptTerms }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
